import { ColDef } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { SavingsOpportunityActionTypeCellRenderer } from "./cell-renderers/SavingsOpportunityActionTypeCellRenderer";
import { SavingsOpportunityServiceCellRenderer } from "./cell-renderers/SavingsOpportunityServiceCellRenderer";
import { SavingsOpportunityImplementationEffortCellRenderer } from "./cell-renderers/SavingsOpportunityImplementationEffortCellRenderer";
import { SavingsOpportunityAccountCellRenderer } from "./cell-renderers/SavingsOpportunityAccountCellRenderer";
import { SavingsOpportunityTagsCellRenderer } from "./cell-renderers/SavingsOpportunityTagsCellRenderer";
import { SavingsOpportunityMonthlySavingsCellRenderer } from "./cell-renderers/SavingsOpportunityMonthlySavingsCellRenderer";
import { SavingsOpportunityBasedOnPastCellRenderer } from "./cell-renderers/SavingsOpportunityBasedOnPastCellRenderer";
import { SavingsOpportunityCloudProviderCellRenderer } from "./cell-renderers/SavingsOpportunityCloudProviderCellRenderer";
import { SavingsOpportunitiesInnerHeader } from "./header/SavingsOpportunitiesInnerHeader";
import { SavingsOpportunitiesPriceInnerHeader } from "./header/SavingsOpportunitiesPriceInnerHeader";
import { getMoneyTypographyCellRenderer } from "../../common/ag-grid/components/columns/getMoneyTypographyCellRenderer";
import { getSimpleTypographyCellRenderer } from "../../common/ag-grid/components/columns/getSimpleTypographyCellRenderer";
import { TaskStatusCellRenderer } from "../../common/ag-grid/components/common-cell-renderers/TaskStatusCellRenderer";
import {
  getSavingsOpportunitiesCostValueGetterByKey,
  getSavingsOpportunitiesStatusesValueGetter,
  savingsOpportunitiesAccountValueGetter,
  savingsOpportunitiesBasedOnPastValueGetter,
  savingsOpportunitiesCloudProviderValueGetter,
  savingsOpportunitiesDescriptionValueGetter,
  savingsOpportunitiesImplementationEffortValueGetter,
  savingsOpportunitiesNameValueGetter,
  savingsOpportunitiesOpportunityTypeValueGetter,
  savingsOpportunitiesServiceValueGetter,
  savingsOpportunitiesTagsValueGetter,
} from "../utils/helpers/valueGetters";
import {
  getSavingsOpportunitiesTaskComparator,
  savingsOpportunitiesAccountComparator,
  savingsOpportunitiesCloudProviderComparator,
  savingsOpportunitiesImplementationEffortComparator,
  savingsOpportunitiesTagsComparator,
} from "../utils/helpers/comparators";
import { keyInsensitiveComparatorFn } from "../../common/ag-grid/utils/helpers/sortingFunctions/keyInsensitiveComparatorFn";
import { SavingsOpportunity, TaskStatus } from "@/services/cloudchipr.api";
import { SavingsOpportunityNameCellRenderer } from "@/components/pages/savings-opportunities/components/cell-renderers/SavingsOpportunityNameCellRenderer";
import { SavingsOpportunityDescriptionCellRenderer } from "@/components/pages/savings-opportunities/components/cell-renderers/SavingsOpportunityDescriptionCellRenderer";
import { BillingCostCell } from "@/components/pages/common/resources-ag-grid/components/column-defs/column-def-cell-renderers/billing-cost/BillingCostCell";
import { SavingsOpportunitiesMonthlySavingsHeader } from "@/components/pages/savings-opportunities/components/header/SavingsOpportunitiesMonthlySavingsHeader";
import { savingsOpportunitiesGroupingOptions } from "@/components/pages/savings-opportunities/utils/constants/savingsOpportunitiesGroupingOptions";
import { savingOpportunitiesDefaultHiddenColumns } from "@/components/pages/savings-opportunities/components/savingOpportunitiesDefaultHiddenColumns";

export const savingsOpportunityAccountColumnField =
  "account.provider_account_id";
export const savingsOpportunityBillingCostColumnField = "billing_cost.total";
export const savingsOpportunityForecastEOMColumnField =
  "forecasted_end_of_month";
export const savingsOpportunityResourceArnColumnField = "resource_arn";
export const savingsOpportunityCloudProviderColumnField = "cloud_provider";

export const getSavingsOpportunitiesTableColumns = (
  taskStatuses?: TaskStatus[],
  enableBillingCostAndForecastedEOMForOpportunities?: boolean,
  hiddenColumns: Set<string> = savingOpportunitiesDefaultHiddenColumns,
): ColDef<SavingsOpportunity>[] => {
  const colDefs: ColDef<SavingsOpportunity>[] = [
    {
      pinned: "left",
      colId: "opportunity_name",
      headerName: "Opportunity Name",
      minWidth: 300,
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      valueGetter: savingsOpportunitiesNameValueGetter,
      cellRenderer: SavingsOpportunityNameCellRenderer,
    },

    {
      hide: hiddenColumns?.has("action_type"),
      field: "action_type",
      headerName: "Opportunity Type",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      cellRenderer: SavingsOpportunityActionTypeCellRenderer,
      valueGetter: savingsOpportunitiesOpportunityTypeValueGetter,
    },

    {
      hide: hiddenColumns?.has("description"),
      field: "description",
      headerName: "Description",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      cellRenderer: SavingsOpportunityDescriptionCellRenderer,
      valueGetter: savingsOpportunitiesDescriptionValueGetter,
      comparator: keyInsensitiveComparatorFn,
    },

    {
      hide: hiddenColumns?.has(savingsOpportunityCloudProviderColumnField),
      field: savingsOpportunityCloudProviderColumnField,
      headerName: "Cloud Provider",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      cellRenderer: SavingsOpportunityCloudProviderCellRenderer,
      valueGetter: savingsOpportunitiesCloudProviderValueGetter,
      comparator: savingsOpportunitiesCloudProviderComparator,
    },

    {
      hide: hiddenColumns?.has("service"),
      field: "service",
      headerName: "Resource Type",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      cellRenderer: SavingsOpportunityServiceCellRenderer,
      valueGetter: savingsOpportunitiesServiceValueGetter,
    },

    {
      hide: hiddenColumns?.has("resource_type"),
      field: "resource_type",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      headerName: "Type",
      cellRenderer: getSimpleTypographyCellRenderer("resource_type"),
    },

    {
      hide: hiddenColumns?.has("recommended_type"),
      field: "recommended_type",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      headerName: "Recommended Type",
      cellRenderer: getSimpleTypographyCellRenderer("recommended_type"),
    },

    {
      hide: hiddenColumns?.has("resource_id"),
      field: "resource_id",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      headerName: "Resource ID/Name",
      cellRenderer: getSimpleTypographyCellRenderer(
        "resource_name",
        "resource_id",
      ),
      valueGetter: ({ data }) => data?.resource_name ?? data?.resource_id,
    },

    {
      hide: hiddenColumns?.has(savingsOpportunityResourceArnColumnField),
      field: savingsOpportunityResourceArnColumnField,
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      headerName: "Resource ARN",
      cellRenderer: getSimpleTypographyCellRenderer(
        savingsOpportunityResourceArnColumnField,
      ),
    },

    {
      hide: hiddenColumns?.has("implementation_effort"),
      field: "implementation_effort",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      headerName: "Implementation Effort",
      cellRenderer: SavingsOpportunityImplementationEffortCellRenderer,
      valueGetter: savingsOpportunitiesImplementationEffortValueGetter,
      comparator: savingsOpportunitiesImplementationEffortComparator,
    },

    {
      hide: hiddenColumns?.has("based_on_past"),
      field: "based_on_past",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      headerName: "Based On Past",
      cellRenderer: SavingsOpportunityBasedOnPastCellRenderer,
      valueGetter: savingsOpportunitiesBasedOnPastValueGetter,
    },

    {
      hide: hiddenColumns?.has("region"),
      field: "region",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      headerName: "Region",
      cellRenderer: getSimpleTypographyCellRenderer("region"),
    },

    {
      hide: hiddenColumns?.has(savingsOpportunityAccountColumnField),
      field: savingsOpportunityAccountColumnField,
      headerName: "Account",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesInnerHeader,
      },
      cellRenderer: SavingsOpportunityAccountCellRenderer,
      valueGetter: savingsOpportunitiesAccountValueGetter,
      comparator: savingsOpportunitiesAccountComparator,
    },

    {
      hide: hiddenColumns?.has("tags"),
      field: "tags",
      headerName: "Tags",
      enableRowGroup: false,
      cellRenderer: SavingsOpportunityTagsCellRenderer,
      valueGetter: savingsOpportunitiesTagsValueGetter,
      comparator: savingsOpportunitiesTagsComparator,
    },

    {
      hide: hiddenColumns?.has("current_monthly_price"),
      field: "current_monthly_price",
      headerName: "Current Monthly Price",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesPriceInnerHeader,
        innerHeaderComponentParams: {
          approximate: true,
        },
      },
      cellRenderer: getMoneyTypographyCellRenderer(
        "current_monthly_price",
        true,
      ),
      valueGetter: getSavingsOpportunitiesCostValueGetterByKey(
        "current_monthly_price",
      ),
    },

    {
      hide: hiddenColumns?.has(savingsOpportunityForecastEOMColumnField),
      field: savingsOpportunityForecastEOMColumnField,
      headerName: "Forecasted EOM",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesPriceInnerHeader,
        innerHeaderComponentParams: {
          approximate: true,
        },
      },
      cellRenderer: getMoneyTypographyCellRenderer(
        savingsOpportunityForecastEOMColumnField,
        true,
      ),
      valueGetter: getSavingsOpportunitiesCostValueGetterByKey(
        savingsOpportunityForecastEOMColumnField,
      ),
    },

    {
      hide: hiddenColumns?.has("estimated_monthly_price"),
      flex: 1,
      field: "estimated_monthly_price",
      minWidth: 300,
      headerName: "Estimated Cost After Savings",
      headerTooltip:
        "Estimated monthly price after implementing the recommendations.",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesPriceInnerHeader,
        innerHeaderComponentParams: {
          approximate: true,
        },
      },
      cellRenderer: getMoneyTypographyCellRenderer(
        "estimated_monthly_price",
        true,
      ),
      valueGetter: getSavingsOpportunitiesCostValueGetterByKey(
        "estimated_monthly_price",
      ),
    },

    {
      hide: hiddenColumns?.has(savingsOpportunityBillingCostColumnField),
      field: savingsOpportunityBillingCostColumnField,
      headerName: "Billing Cost",
      width: 300,
      minWidth: 250,
      maxWidth: 350,
      cellStyle: { height: "100%" },
      cellRenderer: ({ data }: CustomCellRendererProps<SavingsOpportunity>) => {
        return <BillingCostCell billingCost={data?.billing_cost} />;
      },
    },

    {
      hide: hiddenColumns?.has("task"),
      field: "task",
      headerName: "Task Status",
      cellRenderer: ({ data }: CustomCellRendererProps<SavingsOpportunity>) => {
        if (!data?.task) {
          return "-";
        }

        return <TaskStatusCellRenderer task={data.task} />;
      },
      valueGetter: getSavingsOpportunitiesStatusesValueGetter(taskStatuses),
      comparator: getSavingsOpportunitiesTaskComparator(taskStatuses),
    },

    {
      hide: hiddenColumns?.has("monthly_savings"),
      field: "monthly_savings",
      suppressColumnsToolPanel: true,
      pinned: "right",
      lockPinned: true,
      headerName: "Monthly Savings",
      headerComponentParams: {
        innerHeaderComponent: SavingsOpportunitiesMonthlySavingsHeader,
        innerHeaderComponentParams: {
          approximate: true,
        },
      },
      cellRenderer: SavingsOpportunityMonthlySavingsCellRenderer,
      valueGetter:
        getSavingsOpportunitiesCostValueGetterByKey("monthly_savings"),
    },
  ];

  return colDefs
    .filter((colDef) => {
      if (
        (colDef.field === "billing_cost.total" ||
          colDef.field === "forecasted_end_of_month") &&
        !enableBillingCostAndForecastedEOMForOpportunities
      ) {
        return false;
      }

      return true;
    })
    .map((colDef) => {
      const id = (colDef.field ??
        colDef.colId ??
        "") as SavingsOpportunitiesGroupingOptionsType;

      return {
        ...colDef,
        enableRowGroup: !!(id && savingsOpportunitiesGroupingOptions[id]),
      };
    }) as ColDef<SavingsOpportunity>[];
};

type SavingsOpportunitiesGroupingOptionsType =
  keyof typeof savingsOpportunitiesGroupingOptions;
